import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';

interface OnboardingState {
  step: string;
  version: number;
  workspaceName: string;
  websiteUrl: string | null;
  isComplete: boolean;
}

/**
 * BusinessSetup page (R5) — the business flow's first step.
 *
 * Collapses the former type/purpose/website steps into one screen:
 * choosing /start/business already established type=team and
 * purpose=business, so the only things left to capture are the workspace
 * name and an OPTIONAL website. Submitting advances to the outcomes step.
 *
 * The website field is genuinely optional — leaving it blank is an
 * explicit, first-class choice (no separate "skip" affordance needed) and
 * never dead-ends: enrichment is best-effort and the downstream review
 * step is conditional (R4).
 */
export function BusinessSetupPage() {
  const [workspaceName, setWorkspaceName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  useEffect(() => {
    async function loadState() {
      try {
        const response = await fetch('/api/onboarding/state', { credentials: 'include' });
        const data = await response.json();
        if (response.ok && data.state) {
          const state = data.state as OnboardingState;
          if (state.workspaceName) setWorkspaceName(state.workspaceName);
          if (state.websiteUrl) setWebsiteUrl(state.websiteUrl);
        }
      } catch {
        // Non-blocking — the user can still fill the form.
      } finally {
        setIsLoading(false);
      }
    }
    loadState();
  }, []);

  const handleContinue = async () => {
    if (!workspaceName.trim()) return;

    setIsSaving(true);
    setError('');

    try {
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          step: 'setup',
          data: {
            workspaceName: workspaceName.trim(),
            websiteUrl: websiteUrl.trim() || undefined,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to save your workspace setup');
        return;
      }

      navigate(onboardingPath('business', 'outcomes', continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <OnboardingLayout currentStep="setup" workspaceType="team" onboardingMode="business">
        <div className="bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout currentStep="setup" workspaceType="team" onboardingMode="business">
      <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">Set up your business workspace</h1>
          <p className="text-gray-500 mt-2">
            A couple of quick details and we'll start assembling your workspace.
          </p>
        </div>

        <div className="max-w-md mx-auto space-y-6">
          {/* Workspace name */}
          <div>
            <label htmlFor="workspace-name" className="block text-sm font-medium text-gray-700 mb-2">
              Business / workspace name
            </label>
            <input
              id="workspace-name"
              type="text"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder="Acme Co"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-2">
              This is how your workspace appears across every app. You can change it later.
            </p>
          </div>

          {/* Optional website */}
          <div>
            <label htmlFor="website-url" className="block text-sm font-medium text-gray-700 mb-2">
              Website <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="website-url"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://yourcompany.com"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-400 mt-2">
              Add it and we'll analyze your site to pre-fill brand details, audiences, and
              messaging. Skip it by leaving this blank — nothing downstream depends on it.
            </p>
          </div>

          {/* What happens next */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">What happens next</h3>
            <ul className="space-y-1.5 text-sm text-gray-500">
              <li>Choose the outcomes you want this workspace to improve</li>
              <li>Review the plan and pricing assembled from those outcomes</li>
              {websiteUrl.trim() && <li>Review suggestions we found from your website</li>}
              <li>Invite your team and finish setup</li>
            </ul>
          </div>
        </div>

        {error && (
          <div className="max-w-md mx-auto mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="max-w-md mx-auto mt-8 pt-6 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={handleContinue}
            disabled={isSaving || !workspaceName.trim()}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
